import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import '@ant-design/compatible/assets/index.css';

import { Row, message, Alert } from 'antd';
import {
	clearFiltersInvoices,
	fetchInvoices,
	updateFiltersInvoices,
	updatePaginationInvoices,
	updateSortingInvoices,
	replaceFiltersAndSortingInvoices,
	downloadInvoicesCSV,
	fetchInvoiceDownloadFormats,
	downloadInvoiceFormatCSV,
	updateSelection,
	updateInvoiceStatusBulkWrapper,
} from '../../thunks/invoices_thunks_wrapper';
import {
	getEntitiesById,
	getSelectionObject,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import TableHeader from './TableHeader';
import InvoicesListTable from './InvoicesListTable';
import TableFooter from './TableFooter';
import {
	getRecordsForTargetCollection,
	standardInitialStateGenerator,
} from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { unionWith, differenceWith, isEqual } from 'lodash';
import TableSelectionRow from './TableSelectionRow/TableSelectionRow';
import {
	INVOICE_MULTI_SELECT_ACTION_CONFIG_FOR_BUYER,
	INVOICE_MULTI_SELECT_ACTION_CONFIG_FOR_SUPPLIER,
} from './config/multiSelect/multiSelect';
import moment from 'moment';
import DeclineInvoiceForm from '../decline_invoice_form/DeclineInvoiceForm';
import { isSubcontractorInvoiceViewLocation } from '../invoice_utilities/InvoiceUtilities';
import { useFilters } from '../../hooks/useFilters';
import { getInvoiceFilterConfig } from '../../utils/InvoiceFilterUtils';
import {
	brandsRestCrudThunksForBuyer,
	brandsRestCrudThunksForSupplier,
} from '../../thunks/brands_thunks';

const TARGETCOLLECTIONNAME = 'invoicesTable';

function InvoicesTable({
	data,
	error,
	openMoreInDefault,
	updatePagination,
	fetchBrandsCount,
	invoices,
	invoiceSlice,
	fetchData,
	location,
	updateSorting,
	updateFilters,
	clearFilters,
	companyConfig,
	currentUser,
	downloadInvoicesCSV,
	CustomFooter,
	CustomHeader,
	renderInvoice,
	tableStyle,
	history,
	renderEmptyState,
	invoiceDownloadFormats,
	downloadInvoiceFormatCSV,
	fetchInvoiceDownloadFormats,
	targetCollectionName,
	userType,
	filterConfig,
	enableViews,
	replaceFiltersAndSorting,
	preAppliedFilters,
	enableMultiSelect,
	updateSelections,
	updateInvoiceStatuses,
	errors,
	enableBannerMessages,
	//loading,
	enableMessaging,
	backText,
}) {
	const {
		filters: activeFilters,
		pagination,
		sorting: activeSorters,
	} = invoices || standardInitialStateGenerator([targetCollectionName]);
	React.useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const preserve = queryParams.has('preserve');
		if (!preserve || !(data && data.length)) {
			fetchData(location);
			if (!CustomHeader) {
				fetchInvoiceDownloadFormats();
			}
		}
	}, []);

	//Statuses
	const { fetching, updating } = invoiceSlice;
	if (enableMessaging) {
		updating
			? message.open({
					key: 'invoiceTableMessageUpdating',
					type: 'loading',
					content: 'Updating...',
					duration: 60,
			  })
			: message.destroy('invoiceTableMessageUpdating');
	}

	const [brandAvailable, setBrandAvailable] = useState(false);

	useEffect(() => {
		fetchBrandsCount()
			.then((count) => setBrandAvailable(count > 0))
			.catch(() => {});
	}, []);

	const invoiceFilters = useFilters({
		filterTargetCollectionName: 'invoicesAdvancedFilters',
		overviewCollectionName: TARGETCOLLECTIONNAME,
		updateFilters,
	});

	const FILTER_CONFIG = useMemo(() => {
		const defaultConfig = getInvoiceFilterConfig({
			filters: invoiceFilters,
			userType,
			currentUser,
			brandAvailable,
		});
		const advancedFilterConfig =
			filterConfig && filterConfig.filters && filterConfig.filters.length > 0
				? filterConfig.filters.map((filter) => defaultConfig.find((_) => _.fieldName === filter))
				: defaultConfig;
		return {
			...(filterConfig || {}),
			filters: advancedFilterConfig.filter(Boolean),
		};
	}, [invoiceFilters, userType, currentUser, filterConfig, brandAvailable]);

	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [declining, setDeclining] = useState(false);

	const handleSubmitDisputeInvoice = (values) => {
		setDeclining(true);
		updateInvoiceStatuses(
			invoices.selections.map((record) => record.id),
			'disputed',
			Object.keys(invoices.selections[0]),
			{
				disputeNote: {
					text: values.note || '',
					noteAddedBy: nullSafeGet('email', currentUser),
					noteAddedAt: moment.utc(),
				},
			}
		)
			.then((data) => {
				setShowDeclineModal(false);
				message.destroy('invoiceTableMessageUpdating');
				message.open({
					key: 'invoiceTableSuccess',
					type: 'success',
					content: data.length.toString().concat(' records updated'),
					duration: 2,
				});
			})
			.catch((error) => {
				message.open({
					key: 'invoiceTableError',
					type: 'error',
					content: error,
					duration: 10,
				});
			})
			.finally(() => setDeclining(false));
	};
	const assignBulkActionFunction = (action) => {
		switch (nullSafeGet('toStatus', action)) {
			case 'disputed':
				return () => setShowDeclineModal(true);
			default:
				return () =>
					updateInvoiceStatuses(
						invoices.selections.map((record) => record.id),
						action.toStatus,
						Object.keys(invoices.selections[0])
					)
						.then((data) => {
							message.destroy('invoiceTableMessageUpdating');
							message.open({
								key: 'invoiceTableSuccess',
								type: 'success',
								content: data.length.toString().concat(' records updated'),
								duration: 2,
							});
						})
						.catch((error) => {
							message.open({
								key: 'invoiceTableError',
								type: 'error',
								content: error,
								duration: 10,
							});
						});
		}
	};
	let actions = (
		userType === 'buyer'
			? INVOICE_MULTI_SELECT_ACTION_CONFIG_FOR_BUYER
			: INVOICE_MULTI_SELECT_ACTION_CONFIG_FOR_SUPPLIER
	).map((action) => {
		return {
			...action,
			fn: assignBulkActionFunction(action),
		};
	});

	let selectItems = enableMultiSelect
		? {
				onSelectCurrentPage: (records) => {
					let keys = unionWith(
						invoices.selections,
						records.map((rec) => getSelectionObject(rec)),
						isEqual
					);
					updateSelections(targetCollectionName, keys);
				},
				onDeselectCurrentPage: (records) => {
					let keys = differenceWith(
						invoices.selections,
						records.map((rec) => getSelectionObject(rec)),
						isEqual
					);
					updateSelections(targetCollectionName, keys);
				},
				onSelectNone: (e) => {
					updateSelections(targetCollectionName, []);
				},
				selections: invoices.selections,
				rowSelection: {
					onSelect: (record, selected, selectedRows, e) => {
						let isSelected = invoices.selections
							.map((item) => item.id)
							.includes(record.id.toString());
						let keys = !isSelected
							? unionWith(invoices.selections, [getSelectionObject(record)], isEqual)
							: differenceWith(invoices.selections, [getSelectionObject(record)], isEqual);
						updateSelections(targetCollectionName, keys);
					},
					selectedRowKeys: invoices.selections.map((item) => item.id),
					preserveSelectedRowKeys: true,
				},
		  }
		: null;

	return (
		<div>
			{CustomHeader ? (
				<CustomHeader
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					downloadCSV={() => downloadInvoicesCSV({}, activeFilters)}
					counts={invoiceSlice.counts}
					filterConfig={FILTER_CONFIG}
					clearFilters={clearFilters}
					targetCollectionName={targetCollectionName}
					userType={userType}
					pagination={pagination}
					fetching={fetching}
					location={location}
					currentUser={currentUser}
				/>
			) : (
				<TableHeader
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					downloadCSV={() => downloadInvoicesCSV({}, activeFilters)}
					counts={invoiceSlice.counts}
					filterConfig={FILTER_CONFIG}
					clearFilters={clearFilters}
					targetCollectionName={targetCollectionName}
					enableViews={enableViews}
					userType={userType}
					applyFiltersFromView={replaceFiltersAndSorting}
					preAppliedFilters={preAppliedFilters}
					downloadInvoiceFormatCSV={(key) => downloadInvoiceFormatCSV(key, {}, activeFilters)}
					invoiceDownloadFormatRecords={getRecordsForTargetCollection(
						invoiceDownloadFormats,
						'invoiceFormatsIndex'
					)}
					fetching={fetching}
					location={location}
					currentUser={currentUser}
				/>
			)}

			<div style={{ marginTop: 16, marginLeft: 4, background: 'white' }}>
				{enableBannerMessages &&
				nullSafeGet('filters.statuses', invoiceSlice[targetCollectionName]) === 'Draft' ? (
					<Row>
						<Alert
							style={{ width: '100%', padding: '8px 9px', borderWidth: 0 }}
							type={'warning'}
							showIcon
							message={
								<span style={{ paddingLeft: '16px' }}>
									Drafted invoices can't be seen by your customers. Please make sure to submit your
									invoice once work has been completed.
								</span>
							}
						/>
					</Row>
				) : null}
				{enableMultiSelect ? (
					<TableSelectionRow
						selectItems={selectItems}
						actions={actions}
						records={data}
						counts={invoiceSlice.counts}
						updatePagination={updatePagination}
						pagination={pagination}
						updating={updating}
						errors={errors}
						currentUser={currentUser}
					/>
				) : null}
				<DeclineInvoiceForm
					visible={showDeclineModal}
					onCancel={() => setShowDeclineModal(false)}
					onSubmit={handleSubmitDisputeInvoice}
					declining={declining}
				/>
				<InvoicesListTable
					fetching={fetching}
					data={data}
					currentUser={currentUser}
					companyConfig={companyConfig}
					renderInvoice={renderInvoice}
					refreshTable={fetchData}
					tableStyle={tableStyle}
					renderEmptyState={renderEmptyState}
					location={location}
					userType={userType}
					selectItems={selectItems}
					enableMultiSelect={enableMultiSelect}
					backText={backText}
					targetCollectionName={targetCollectionName}
				/>
				{CustomFooter ? (
					<CustomFooter
						updatePagination={updatePagination}
						pagination={{ ...pagination, current: 1, pageSize: 3 }}
						filters={activeFilters}
						sorter={activeSorters}
						location={location}
						history={history}
						enableMultiSelect={enableMultiSelect}
						openMoreInDefault={openMoreInDefault}
						userType={userType}
					/>
				) : (
					<Row justify="end">
						<TableFooter
							records={data}
							//enableMultiSelect={enableMultiSelect}
							updatePagination={updatePagination}
							pagination={
								pagination && pagination.total && pagination.total !== 0 ? pagination : null
							}
							//selectItems={selectItems}
						/>
					</Row>
				)}
			</div>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	handleQueryParams: ownProps.handleQueryParams,
	errors: state.session.errors,
	loading: state.session.loading,
	invoices: state.invoices[ownProps.targetCollectionName || TARGETCOLLECTIONNAME],
	invoiceSlice: state.invoices,
	companyConfig: state.company_config.detail,
	targetCollectionName: ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
	data: getEntitiesById(
		state.invoices[ownProps.targetCollectionName || TARGETCOLLECTIONNAME]
			? state.invoices[ownProps.targetCollectionName || TARGETCOLLECTIONNAME].recordsIds || []
			: [],
		state.invoices.records
	),
	invoiceDownloadFormats: state.invoice_download_formats,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchData: (location) =>
		dispatch(
			fetchInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				location,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting,
				ownProps.initialFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updatePagination: (pagination) =>
		dispatch(
			updatePaginationInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSorting: (sorting) =>
		dispatch(
			updateSortingInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	updateFilters: (filters) =>
		dispatch(
			updateFiltersInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSelections: (targetCollectionName, selections) =>
		dispatch(
			updateSelection(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				selections
			)
		),
	replaceFiltersAndSorting: (filters, sorting) =>
		dispatch(
			replaceFiltersAndSortingInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	clearFilters: () =>
		dispatch(
			clearFiltersInvoices(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	downloadInvoicesCSV: (params, filters) =>
		dispatch(
			downloadInvoicesCSV(ownProps.userType, isSubcontractorInvoiceViewLocation(ownProps.location))(
				params,
				filters
			)
		),
	fetchInvoiceDownloadFormats: (params) =>
		dispatch(fetchInvoiceDownloadFormats(ownProps.userType)(params)),
	downloadInvoiceFormatCSV: (formatName, params, filters) =>
		dispatch(downloadInvoiceFormatCSV(ownProps.userType)(formatName, params, filters)),
	updateInvoiceStatuses: (ids, status, invoiceParams, additionalParams = {}) =>
		dispatch(
			updateInvoiceStatusBulkWrapper(ownProps.userType)(
				ids,
				status,
				TARGETCOLLECTIONNAME,
				invoiceParams,
				additionalParams
			)
		),
	fetchBrandsCount: (params) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.countBy
				: brandsRestCrudThunksForBuyer.countBy;
		return dispatch(fnToCall(params, 'all'));
	},
});

const InvoicesHOC = withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoicesTable));

export default connect(
	(state) => ({
		userType: state.session.userType,
		currentUser: state.session.currentUser,
	}),
	(dispatch, ownProps) => ({})
)(InvoicesHOC);
